import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef
} from 'react'
import {noPropagate} from '@containers/hooks'
import {
  TwMenu,
  TwMenuContainer,
  TwMenuCustomItem,
  TwMenuScrollContainer,
  TwMenuSeparator,
  TwMenuSeparatorEmpty
} from './containers'
import {MenuItem} from './menu-item'
import {MenuInstance, MenuItemInstance, MenuProps} from './types'

export const Menu = forwardRef<MenuInstance, MenuProps>(
  (
    {items, className, disabled, onItemClicked, popupMenu}: MenuProps,
    forwardedRef
  ) => {
    const refMap = useRef<Map<string, MenuItemInstance>>(new Map())
    const close = useCallback(() => {
      for (const el of refMap.current.values()) {
        el.close()
      }
    }, [refMap])
    const closeOther = useCallback(
      (id: string) => {
        for (const [elId, el] of refMap.current.entries()) {
          elId !== id && el.close()
        }
      },
      [refMap]
    )

    const itemClicked = useCallback(() => {
      onItemClicked?.()
      close()
    }, [onItemClicked, close])

    useImperativeHandle(forwardedRef, () => ({close}), [close])

    return (
      <TwMenuContainer
        $popupMenu={popupMenu}
        className={className}
        onClick={noPropagate}
        onDoubleClick={noPropagate}
      >
        <TwMenuScrollContainer $popupMenu={popupMenu} forceCustomScroll>
          <TwMenu>
            {items.map((item, ix) => {
              if ('separator' in item) {
                return item.empty ? (
                  <TwMenuSeparatorEmpty key={'separator-' + ix} />
                ) : (
                  <TwMenuSeparator key={'separator-' + ix} />
                )
              } else if ('element' in item) {
                return (
                  <TwMenuCustomItem key={item.id} onMouseOver={close}>
                    {item.element}
                  </TwMenuCustomItem>
                )
              } else {
                return (
                  <MenuItem
                    {...item}
                    ref={el => el && refMap.current.set(item.id, el)}
                    key={item.id}
                    disabled={disabled || item.disabled}
                    onHover={closeOther}
                    onItemClicked={itemClicked}
                    popupMenu={popupMenu}
                  />
                )
              }
            })}
          </TwMenu>
        </TwMenuScrollContainer>
      </TwMenuContainer>
    )
  }
)
